/**
 * CreativeReviewPage — shell for the "Creative Review" admin section.
 *
 * Two sub-views (driven by the route, not local state, so links are
 * shareable): Runs (dashboard + history) and Tuning (brief-prompt editor
 * + dry-run simulate). Client comes from the route / client store; org
 * from getOrgUid().
 */
import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import { Activity, Wand2, FileText } from 'lucide-react';
import { getOrgUid } from '@/services/api';
import { useResolvedClientUid } from '@/components/pdo/utils';
import { EmptyState } from '@/components/pdo/EmptyState';
import { RunsView } from './RunsView';
import { TuningView } from './TuningView';
import { BriefsView } from './BriefsView';

export function CreativeReviewPage({
  tab,
}: {
  tab: 'runs' | 'briefs' | 'tuning';
}) {
  const clientUid = useResolvedClientUid('/admin/creative-review');
  const orgUid = getOrgUid();

  const tabs = [
    { key: 'runs', label: 'Runs', icon: Activity },
    { key: 'briefs', label: 'Briefs', icon: FileText },
    { key: 'tuning', label: 'Tuning', icon: Wand2 },
  ] as const;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          Creative Review
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Review, administer, and tune the Creative Review agent —
          organization{' '}
          <span className="font-mono text-slate-700">{orgUid}</span>
          {clientUid && (
            <>
              {' · client '}
              <span className="font-mono text-slate-700">{clientUid}</span>
            </>
          )}
        </p>
      </header>

      <nav className="flex gap-1 border-b border-slate-200 mb-6">
        {tabs.map((t) => (
          <NavLink
            key={t.key}
            to={`/admin/creative-review/${clientUid ?? ''}/${t.key}`}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === t.key
                ? 'border-brand-teal text-brand-teal'
                : 'border-transparent text-slate-500 hover:text-slate-800',
            )}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </NavLink>
        ))}
      </nav>

      {!clientUid ? (
        <EmptyState
          title="Select a client"
          hint="Pick a client from the selector to view Creative Review runs and tuning."
        />
      ) : tab === 'runs' ? (
        <RunsView clientUid={clientUid} />
      ) : tab === 'briefs' ? (
        <BriefsView clientUid={clientUid} />
      ) : (
        <TuningView orgUid={orgUid} clientUid={clientUid} />
      )}
    </div>
  );
}
